import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { gridContainerVariants, getGridItemVariants } from '@/lib/animations';

type Collection = 'authority' | 'innovation' | 'boutique' | 'all';

interface FeaturedDomain {
  name: string;
  category: string;
  collection: Collection;
  slug: string;
  useCase: string;
  pricePerMonth?: number;
  isHero?: boolean;
}

const FEATURED_DOMAINS: FeaturedDomain[] = [
  {
    name: 'GainName.com',
    category: 'Branding',
    collection: 'boutique',
    slug: 'gainname',
    useCase: 'Built for a naming or brand consultancy whose whole promise is helping clients land the right name.',
    isHero: true,
  },
  {
    name: 'LegalAxis.com',
    category: 'Legal',
    collection: 'authority',
    slug: 'legalaxis',
    useCase: 'Ideal for law firms and legal consultancies that need a name that commands respect.',
    pricePerMonth: 399,
  },
  {
    name: 'FractionalHR.io',
    category: 'HR',
    collection: 'innovation',
    slug: 'fractionalhr',
    useCase: 'Built for fractional HR services and people-ops consultants targeting tech companies.',
    pricePerMonth: 349,
  },
  {
    name: 'BrieflyBrand.com',
    category: 'Branding',
    collection: 'boutique',
    slug: 'brieflybrand',
    useCase: 'A memorable name for creative agencies that specialize in brand storytelling.',
    pricePerMonth: 279,
  },
  {
    name: 'FinanceFlow.ai',
    category: 'Finance',
    collection: 'innovation',
    slug: 'financeflow',
    useCase: 'Perfect for AI-powered financial advisory and automated accounting services.',
    pricePerMonth: 449,
  },
  {
    name: 'TrustLegal.com',
    category: 'Legal',
    collection: 'authority',
    slug: 'trustlegal',
    useCase: 'For established law practices that want a name that immediately signals credibility.',
    pricePerMonth: 499,
  },
  {
    name: 'DesignCraft.io',
    category: 'Design',
    collection: 'boutique',
    slug: 'designcraft',
    useCase: 'Tailored for design studios that emphasize craftsmanship and attention to detail.',
    pricePerMonth: 329,
  },
  {
    name: 'AccountPro.com',
    category: 'Accounting',
    collection: 'authority',
    slug: 'accountpro',
    useCase: 'Professional accounting firms that need a straightforward, trustworthy domain name.',
    pricePerMonth: 379,
  },
];

const COLLECTIONS = [
  {
    id: 'authority' as Collection,
    label: 'Authority Collection',
    subtext: "For legal, finance, accounting, and other 'trust me with serious decisions' services.",
  },
  {
    id: 'innovation' as Collection,
    label: 'Innovation Collection',
    subtext: 'For tech-forward, productized, or fractional services—often with .io and .ai options.',
  },
  {
    id: 'boutique' as Collection,
    label: 'Boutique Collection',
    subtext: 'For creative studios and lifestyle-driven brands that want to feel crafted, not corporate.',
  },
];

export function HomeFeaturedDomainsBento() {
  const [selectedCollection, setSelectedCollection] = useState<Collection>('all');
  const headingReveal = useRevealOnce({ amount: 0.25 });
  const gridReveal = useRevealOnce({ amount: 0.25 });

  const filteredDomains = selectedCollection === 'all' 
    ? FEATURED_DOMAINS 
    : FEATURED_DOMAINS.filter(domain => domain.collection === selectedCollection);

  return (
    <section id="featured-domains" className="relative w-full py-16 md:py-20 bg-slate-950 text-white overflow-hidden">
      {/* Decorative background elements */}
      <div className="pointer-events-none absolute top-1/4 left-1/4 w-96 h-96 bg-[#0000FF]/10 rounded-full blur-3xl opacity-50" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#0000FF]/10 rounded-full blur-3xl opacity-50" aria-hidden="true" />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgb(148 163 184) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }}
        aria-hidden="true"
      />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Heading and intro */}
          <motion.div
            ref={headingReveal.ref}
            className="text-center mb-12 md:mb-16"
            variants={gridContainerVariants}
            initial="hidden"
            animate={headingReveal.controls}
          >
            <motion.h2
              variants={getGridItemVariants(0)}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 md:mb-8 text-white"
            >
              Premium domains curated for service businesses.
            </motion.h2>
            <motion.p
              variants={getGridItemVariants(1)}
              className="text-lg md:text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed"
            >
              These aren't random keyword lists. Every domain here is hand-picked for consultants, agencies, and professional firms—so when someone hears it once, they can remember it, type it, and trust it.
            </motion.p>
          </motion.div>

          {/* Collection filter pills */}
          <motion.div
            className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mb-10 md:mb-12"
            variants={gridContainerVariants}
            initial="hidden"
            animate={headingReveal.controls}
          >
            {COLLECTIONS.map((collection, index) => (
              <motion.button
                key={collection.id}
                variants={getGridItemVariants(index + 2)}
                onClick={() => setSelectedCollection(collection.id)}
                className={`px-6 py-3 rounded-full text-sm md:text-base font-semibold transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 ${
                  selectedCollection === collection.id
                    ? 'bg-blue-600 text-white border-2 border-blue-500'
                    : 'bg-slate-900 text-slate-300 border-2 border-slate-800 hover:border-blue-500/50 hover:text-white'
                }`}
                aria-pressed={selectedCollection === collection.id}
              >
                {collection.label}
              </motion.button>
            ))}
          </motion.div>

          {/* Bento grid */}
          <motion.div
            ref={gridReveal.ref}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 auto-rows-fr gap-4 md:gap-6 mb-12 md:mb-16"
            variants={gridContainerVariants}
            initial="hidden"
            animate={gridReveal.controls}
          >
            {filteredDomains.map((domain, index) => (
              <motion.div
                key={domain.slug}
                variants={getGridItemVariants(index)}
                className={`group ${domain.isHero ? 'md:col-span-2 md:row-span-2' : ''}`}
              >
                <Link
                  to={`/domain/${domain.name.toLowerCase()}`}
                  className="block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 rounded-xl"
                >
                  <div className={`relative h-full rounded-xl border border-slate-800 p-6 md:p-8 transition-all duration-300 hover:scale-[1.02] hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/10 ${
                    domain.isHero ? 'bg-slate-900' : 'bg-slate-800/50'
                  }`}>
                    {/* Scarcity badge */}
                    <div className="absolute top-4 right-4 px-3 py-1 text-xs font-bold text-blue-400 bg-blue-950/50 border border-blue-500/30 rounded-full">
                      1 of 1
                    </div>

                    {/* Domain name */}
                    <h3 className={`font-bold mb-3 md:mb-4 text-white ${
                      domain.isHero ? 'text-3xl md:text-4xl' : 'text-xl md:text-2xl'
                    }`}>
                      {domain.name}
                    </h3>

                    {/* Use-case line */}
                    <p className={`text-slate-400 mb-4 md:mb-6 leading-relaxed ${
                      domain.isHero ? 'text-base md:text-lg' : 'text-sm md:text-base'
                    }`}>
                      {domain.useCase}
                    </p>

                    {/* Category badge */}
                    <div className="inline-block px-3 py-1 text-xs font-semibold text-slate-300 bg-slate-700/50 rounded-full mb-4 md:mb-6">
                      {domain.category}
                    </div>

                    {/* Status/pricing line */}
                    <p className="text-sm md:text-base text-slate-400 mb-4">
                      {domain.pricePerMonth 
                        ? `Buy · Lease-to-own · From $${domain.pricePerMonth}/mo`
                        : 'Buy · Lease-to-own available'
                      }
                    </p>

                    {/* Service upsell link */}
                    <Link
                      to="/services#starter"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center text-xs md:text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                    >
                      Add a done-for-you launch from $2,997 →
                    </Link>

                    {/* Gradient border glow on hover */}
                    <div
                      className="absolute -inset-[1px] rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none blur-sm bg-gradient-to-r from-blue-500/20 via-blue-500/30 to-blue-500/20"
                      aria-hidden="true"
                    />
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          {/* Recently acquired strip */}
          <motion.div
            className="text-center"
            variants={gridContainerVariants}
            initial="hidden"
            animate={headingReveal.controls}
          >
            <motion.p
              variants={getGridItemVariants(3)}
              className="text-sm md:text-base text-slate-400"
            >
              <span className="text-slate-500">Recently acquired by other founders:</span>{' '}
              <span className="text-slate-300">LegalAxis.com · FractionalHR.io · BrieflyBrand.com</span>
            </motion.p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

